import {
  ClaimRecord,
  ClaimRecordLifeCycle,
  ClaimRecordStatus,
  CustomerFacingClaimRecordStatus,
} from '@typesApp/ClaimRecord'
import { ClaimDetails } from '@typesApp/ClaimDetails'
import { ClaimStatus, ClaimStatusSummary, CustomerFacingClaimStatus } from '@typesApp/ClaimData'
import { EligibleProductStatus, ProductBundle } from '@typesApp/Product'
import { QuoteErrorCode, QuoteErrorType } from '@typesApp/QuoteData'
import { isClaimNullifyPeriodExpired } from './claimNullifyPeriod'
import { formatPrice } from './formatPrice'
import { isProductInsuranceEligible } from './isProductInsuranceEligible'

const NULLIFIED_STATUS_LIST = [
  ClaimRecordStatus.CLAIM_NULLIFIED,
  ClaimRecordStatus.FOR_MANUAL_NULLIFY,
  ClaimRecordStatus.CLAIM_NULLIFY_SENT,
  ClaimRecordStatus.CLAIM_NULLIFY_EVENT_SENT,
  ClaimRecordStatus.CLAIM_NULLIFY_REQUIRED,
]

const CLOSED_DELETED_STATUS_LIST = [
  ClaimRecordStatus.CLOSED,
  ClaimRecordStatus.CLOSE_REQUIRED,
  ClaimRecordStatus.DELETED,
  ClaimRecordStatus.DELETE_IN_PROGRESS,
]

const isInsufficientBalanceError = (claimRecord: ClaimRecord): boolean => {
  const lastResult = claimRecord?.claimData?.quoteSummaries?.find(s => s.lastQuote)
  return lastResult
    ? lastResult.errorType === QuoteErrorType.CUSTOMER_FACING_ERROR &&
        lastResult.errorCode === QuoteErrorCode.INSUFFICIENT_BALANCE
    : false
}

export const getClaimDetails = (claimRecord: ClaimRecord): ClaimDetails | null => {
  if (!claimRecord?.claimData) {
    return null
  }

  const isNullifyExpired = claimRecord?.lastModified ? isClaimNullifyPeriodExpired(claimRecord.lastModified) : false
  const isInsufficientBalance = isInsufficientBalanceError(claimRecord)
  const claimRecordStatus = claimRecord?.status
  const customerFacingStatus = getCustomerFacingStatus(claimRecord)
  const claimRecordId = claimRecord.id
  const cartId = claimRecord?.cartId
  const customerEmail = claimRecord?.customerEmail
  const customerNumber = claimRecord?.customerNumber
  const claimStatus = claimRecord?.claimData?.claim?.status
  const claimPayee = claimRecord?.claimData?.claim?.payee
  const orderSubtotal = claimRecord.orderSubtotal
  const claimAmount = formatPrice(claimRecord?.claimData?.claim?.confirmedAmount?.amount ?? 0)
  const residualAmount = formatPrice(
    (orderSubtotal?.amount ?? 0) - (claimRecord?.claimData?.claim?.confirmedAmount?.amount ?? 0)
  )
  const insurerName = getInsurerName(claimRecord)
  const lifeCycle = claimRecord?.lifeCycle || ClaimRecordLifeCycle.SESSION_ONLY
  const normalizedLocale = claimRecord?.locale?.replace('_', '-')
  const useInsurance = claimRecord?.claimData?.useInsuranceStatus

  return {
    isNullifyExpired,
    isInsufficientBalance,
    claimRecordStatus,
    claimStatus,
    claimPayee,
    claimAmount,
    residualAmount,
    insurerName,
    customerFacingStatus,
    claimRecordId,
    cartId,
    customerEmail,
    customerNumber,
    lifeCycle,
    orderSubtotal,
    normalizedLocale,
    useInsurance,
  }
}

export const getCustomerFacingStatus = (claimRecord: ClaimRecord): CustomerFacingClaimRecordStatus => {
  const claimRecordStatus = claimRecord.status

  const statusMap = {
    CLAIM_ACCEPTED: CustomerFacingClaimRecordStatus.ACCEPTED,
    CLAIM_REJECTED: CustomerFacingClaimRecordStatus.REJECTED,
    CLAIM_ACKNOWLEDGED: CustomerFacingClaimRecordStatus.ACKNOWLEDGED_NULLIFIED,
    RESTRICTED: CustomerFacingClaimRecordStatus.LIMIT_REACHED,
    CLAIM_NULLIFY_REJECTED: CustomerFacingClaimRecordStatus.NULLIFY_REJECTED,
    FOR_MANUAL_NULLIFY: CustomerFacingClaimRecordStatus.NULLIFY_REJECTED,
  } as Record<ClaimRecordStatus, CustomerFacingClaimRecordStatus>

  return (
    statusMap[claimRecordStatus] ||
    (claimRecordStatus ? CustomerFacingClaimRecordStatus.IN_PROGRESS : CustomerFacingClaimRecordStatus.DEFAULT)
  )
}

export const getInsurerName = (claimRecord: ClaimRecord | undefined): string | undefined => {
  return claimRecord?.claimData?.primaryCoverage?.insurer?.name
}

export const allowStartClaim = (customerFacingStatus: CustomerFacingClaimRecordStatus): boolean => {
  return ![CustomerFacingClaimRecordStatus.ACCEPTED, CustomerFacingClaimRecordStatus.LIMIT_REACHED].includes(
    customerFacingStatus
  )
}

export const getEligibleProductStatus = (products: ProductBundle[]): EligibleProductStatus => {
  if (!products.length) {
    return EligibleProductStatus.NA
  }

  const eligibleProducts = products.filter(isProductInsuranceEligible)
  return eligibleProducts.length > 0 ? EligibleProductStatus.ELIGIBLE : EligibleProductStatus.NOT_ELIGIBLE
}

export const getLastClaimStatusSummary = (claimRecord?: ClaimRecord): ClaimStatusSummary => {
  const lastResult = claimRecord?.claimData?.claimSummaries?.find(s => s.lastClaim)
  return lastResult
    ? {
        ...lastResult,
        claimRecordStatus: claimRecord?.status,
      }
    : {
        status: claimRecord?.claimData?.claim?.status ?? ClaimStatus.NONE,
        customerFacingStatus: claimRecord?.claimData?.claim?.customerFacingStatus ?? CustomerFacingClaimStatus.NONE,
        errorType: claimRecord?.claimData?.claim?.errorType,
        errorCode: claimRecord?.claimData?.claim?.errorCode,
        errorMessage: claimRecord?.claimData?.claim?.errorMessage,
        claimRecordStatus: claimRecord?.status,
      }
}

export const isNullifiedStatus = (status: ClaimRecordStatus | undefined): boolean => {
  return status ? NULLIFIED_STATUS_LIST.includes(status) : false
}

export const isClosedOrDeletedStatus = (status: ClaimRecordStatus | undefined): boolean => {
  return status ? CLOSED_DELETED_STATUS_LIST.includes(status) : false
}

export const isForManualNullify = (status: ClaimRecordStatus | undefined): boolean => {
  return status === ClaimRecordStatus.FOR_MANUAL_NULLIFY
}

export const isForManualNullifyOrClosed = (status: ClaimRecordStatus | undefined): boolean => {
  return status === ClaimRecordStatus.FOR_MANUAL_NULLIFY || status === ClaimRecordStatus.CLOSED
}

export const isQuoteRequestedStatus = (status: ClaimRecordStatus | undefined): boolean => {
  return status === ClaimRecordStatus.QUOTE_REQUESTED
}

export const isSubmittedStatus = (status: ClaimRecordStatus | undefined): boolean => {
  return status === ClaimRecordStatus.CLAIM_SUBMITTED
}

export const isRestrictedStatus = (claimRecordStatus: ClaimRecordStatus | undefined): boolean => {
  return claimRecordStatus === ClaimRecordStatus.RESTRICTED
}

export const isSystemRefusedEventSentStatus = (claimRecord: ClaimRecord): boolean => {
  return claimRecord?.status === ClaimRecordStatus.QUOTE_SYSTEM_REFUSED_EVENT_SENT
}

export const shouldRefreshCartTotals = (claimRecord?: ClaimRecord): boolean => {
  return claimRecord?.claimData?.refreshCartTotalRequired === true
}

export const isClaimAcceptedStatus = (status: ClaimRecordStatus | undefined): boolean => {
  return status === ClaimRecordStatus.CLAIM_ACCEPTED
}

export const isClaimAcceptedStatusWithOrderPlaced = (claimRecord: ClaimRecord | undefined): boolean => {
  return claimRecord?.status === ClaimRecordStatus.CLAIM_ACCEPTED && Boolean(claimRecord?.claimData?.claim?.orderNumber)
}

export const shouldCheckEligibleProducts = (status: ClaimRecordStatus | undefined): boolean => {
  if (!status) return true

  return ![
    ClaimRecordStatus.CLAIM_ACCEPTED,
    ClaimRecordStatus.CLAIM_ACKNOWLEDGED,
    ClaimRecordStatus.RESTRICTED,
    ClaimRecordStatus.QUOTE_PENDING,
    ClaimRecordStatus.QUOTE_REQUESTED,
    ClaimRecordStatus.FOR_MANUAL_NULLIFY,
    ClaimRecordStatus.CLAIM_NULLIFY_EVENT_SENT,
    ClaimRecordStatus.CLAIM_NULLIFY_REJECTED,
    ClaimRecordStatus.DELETE_IN_PROGRESS,
  ].includes(status)
}
